import { codeNameLabel } from '@/shared/utils/codeNameLabel';
import { env } from '@/app/config/env';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { delay } from '@/shared/utils/asyncUtils';
import { MOCK_LATENCY_MS } from '../constants';
import {
  mockCities,
  mockCountries,
  mockCreateOrganization,
  mockEmployeeCountRanges,
  mockGetOrganization,
  mockNextOrganizationCode,
  mockPhoneCountryCodes,
  mockStates,
  mockUpdateOrganization,
} from './mocks/organizationStep1.mock';
import type {
  EmployeeCountRange,
  NextOrganizationCode,
  OrganizationRecord,
  OrganizationWritePayload,
  PagedContent,
  PhoneCountryCodeItem,
  ReferenceItem,
  SelectOption,
} from '../models';

/**
 * Step 1's API surface, and nothing beyond it.
 *
 * Every endpoint here is one of the eight in
 * mdfiles/organization-setup-step1-apiflow.md section 7. Paths are relative to
 * VITE_API_BASE_URL, which already carries the /api/v1 prefix. The bearer
 * token is attached by the shared interceptors, so no endpoint sets it.
 */

/** Section 4: reference lists are paged; 200 covers every real list. */
const REFERENCE_PAGE_SIZE = 200;
const REFERENCE_SORT = 'name,asc';

const FALLBACK_MESSAGE = 'Server error, please try again';

const step1Api = {
  getCountries: () =>
    apiClient.get<ApiEnvelope<PagedContent<ReferenceItem>>>('/countries', {
      params: { size: REFERENCE_PAGE_SIZE, sort: REFERENCE_SORT },
    }),
  /*
    One country, for the currency it names. The listing carries only {id, name} through `ReferenceItem`,
    and step 2 needs the currency the country is denominated in -- see getCountryCurrencyId.
  */
  getCountry: (countryId: number) =>
    apiClient.get<ApiEnvelope<{ currencyId?: number | null }>>(`/countries/${String(countryId)}`, {
      /* A country the caller may not read is not a reason to fail the step; the default currency stands. */
      _allowForbidden: true,
    }),
  getStates: (countryId: number) =>
    apiClient.get<ApiEnvelope<PagedContent<ReferenceItem>>>('/states', {
      params: { countryId, size: REFERENCE_PAGE_SIZE, sort: REFERENCE_SORT },
    }),
  getCities: (stateId: number) =>
    apiClient.get<ApiEnvelope<PagedContent<ReferenceItem>>>('/cities', {
      params: { stateId, size: REFERENCE_PAGE_SIZE, sort: REFERENCE_SORT },
    }),
  getEmployeeCountRanges: () =>
    apiClient.get<ApiEnvelope<EmployeeCountRange[]>>('/organizations/employee-count-ranges'),
  getPhoneCountryCodes: () =>
    apiClient.get<ApiEnvelope<PagedContent<PhoneCountryCodeItem>>>('/phone-country-codes', {
      params: { size: REFERENCE_PAGE_SIZE },
    }),
  getNextCode: () =>
    apiClient.get<ApiEnvelope<NextOrganizationCode>>('/organizations/next-code'),
  getOrganization: (organizationCode: string, background = false) =>
    apiClient.get<ApiEnvelope<OrganizationRecord>>(
      `/organizations/${encodeURIComponent(organizationCode)}`,
      background ? { _allowForbidden: true } : undefined,
    ),
  createOrganization: (payload: OrganizationWritePayload) =>
    apiClient.post<ApiEnvelope<OrganizationRecord>>('/organizations', payload),
  updateOrganization: (organizationCode: string, payload: OrganizationWritePayload) =>
    apiClient.put<ApiEnvelope<OrganizationRecord>>(
      `/organizations/${encodeURIComponent(organizationCode)}`,
      payload,
    ),
};

/** `{ id, name }` -> what the PrimeReact dropdowns bind to. */
function toOptions(items: readonly ReferenceItem[]): SelectOption[] {
  /* `CODE - NAME` where the row has a code, the name alone where it has none; the value stays the id. */
  return items.map((item) => ({ label: codeNameLabel(item.code, item.name), value: String(item.id) }));
}

/* -- reference data (section 4) -------------------------------------------- */

export async function getCountries(): Promise<SelectOption[]> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return toOptions(mockCountries());
  }
  return toOptions(unwrapEnvelope(await step1Api.getCountries(), FALLBACK_MESSAGE).content);
}

/**
 * The currency a country is denominated in, or null where it names none.
 *
 * <p>Asked for so that the site configuration can default its currency from the organization's country
 * rather than from whichever currency happens to be first in the list. Null on any failure: a currency
 * that cannot be resolved is a default not applied, never a step that will not load.
 */
export async function getCountryCurrencyId(countryId: number): Promise<number | null> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return null;
  }
  try {
    return unwrapEnvelope(await step1Api.getCountry(countryId), FALLBACK_MESSAGE).currencyId ?? null;
  } catch {
    return null;
  }
}

/**
 * Section 4.2: an empty `content[]` means no states are mapped to that country
 * yet. The caller decides what to do with an empty list -- this just reports it.
 */
export async function getStates(countryId: number): Promise<SelectOption[]> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return toOptions(mockStates(countryId));
  }
  return toOptions(
    unwrapEnvelope(await step1Api.getStates(countryId), FALLBACK_MESSAGE).content,
  );
}

/** Section 4.3: a city hangs off its state directly, hence `stateId`. */
export async function getCities(stateId: number): Promise<SelectOption[]> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return toOptions(mockCities(stateId));
  }
  return toOptions(unwrapEnvelope(await step1Api.getCities(stateId), FALLBACK_MESSAGE).content);
}

export async function getEmployeeCountRanges(): Promise<EmployeeCountRange[]> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockEmployeeCountRanges();
  }
  return unwrapEnvelope(await step1Api.getEmployeeCountRanges(), FALLBACK_MESSAGE);
}

/**
 * The dial codes behind both phone fields. Labelled "+91 India" so the code
 * -- the part the user is choosing -- reads first, matching the reference,
 * which shows the dial code beside a flag rather than the country name alone.
 */
export async function getPhoneCountryCodes(): Promise<SelectOption[]> {
  const rows = env.ENABLE_MOCKS
    ? (await delay(MOCK_LATENCY_MS), mockPhoneCountryCodes())
    : unwrapEnvelope(await step1Api.getPhoneCountryCodes(), FALLBACK_MESSAGE).content;

  return rows.map((row) => ({
    label: codeNameLabel(row.code, row.name),
    value: String(row.id),
  }));
}

/**
 * Section 4.5. Advisory only: a concurrent create can take this code, so it
 * fills the read-only preview and is never sent back or persisted.
 */
export async function getNextOrganizationCode(): Promise<string> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockNextOrganizationCode();
  }
  return unwrapEnvelope(await step1Api.getNextCode(), FALLBACK_MESSAGE).organizationCode;
}

/* -- organization CRUD (section 5) ----------------------------------------- */

export interface GetOrganizationOptions {
  /**
   * True for the login bootstrap, which reads the organization before any screen asks for it.
   *
   * `GET /organizations/{code}` requires `Organization:READ`, and most roles do not hold it — an
   * Initiator holds Dashboard, RFQ and Supplier grants and nothing else. Without this flag that 403 goes
   * through the global handler, which navigates the whole app to /unauthorized: signing in as such a role
   * landed on "You do not have access to that page" before any page had been asked for. The caller
   * already treats a failure as "no record", so the refusal belongs to it, not to the shell.
   *
   * Leave it off wherever the user opened the screen — there a 403 really does mean the page is barred.
   */
  readonly background?: boolean;
}

/** Section 5.3. Keyed by organizationCode, never by the numeric id. */
export async function getOrganization(
  organizationCode: string,
  options: GetOrganizationOptions = {},
): Promise<OrganizationRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockGetOrganization(organizationCode);
  }
  return unwrapEnvelope(
    await step1Api.getOrganization(organizationCode, options.background === true),
    FALLBACK_MESSAGE,
  );
}

/** Section 5.1. Status defaults to Pending (10) server-side, because statusId is never sent. */
export async function createOrganization(
  payload: OrganizationWritePayload,
): Promise<OrganizationRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockCreateOrganization(payload);
  }
  return unwrapEnvelope(await step1Api.createOrganization(payload), FALLBACK_MESSAGE);
}

/** Section 5.4. Full replacement -- an omitted field is cleared, not kept. */
export async function updateOrganization(
  organizationCode: string,
  payload: OrganizationWritePayload,
): Promise<OrganizationRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockUpdateOrganization(organizationCode, payload);
  }
  return unwrapEnvelope(
    await step1Api.updateOrganization(organizationCode, payload),
    FALLBACK_MESSAGE,
  );
}
